<template>
  <bizgain-component-layout
    :is-next-btn-enabled="true"
    :col-width="{
      'mx-5 px-4': !$vuetify.breakpoint.xsOnly,
      'mx-2': $vuetify.breakpoint.xsOnly
    }"
    :is-content-top="true"
    view-port-height="170px"
  >
    <template v-slot:heading>
      {{ $t('message.bizgainOnboarding.businessLocationHeading') }}
    </template>
    <template v-slot:sub-heading>
      {{ $t('message.bizgainOnboarding.businessLocationSubHeading') }}
    </template>
    <template v-slot:content>
      <v-flex xs12 class="business-location-details">
        <currency-preference-dialog
          :dialog.sync="isCurrencyPreferenceDialog"
          @regionSelected="handleRegionSelected"
        />
        <v-flex xs12>
          <div class="label">
            {{ $tc('message.bizgainOnboarding.businessName') }}
          </div>
          <v-text-field
            :placeholder="$tc('message.bizgainOnboarding.businessName')"
            class="pt-0 mt-0"
            name="businessName"
            counter="25"
            v-model="name"
            autocapitalize="off"
            data-vv-as="Business Name"
            v-validate.initial="'required|max:25|emojiValidation'"
            @focus="clearErrorByField($event, 'businessName')"
            :error-messages="errors.first('businessName')"
          />
        </v-flex>
        <v-flex
          xs12
          sm12
          md12
          lg12
          v-show="false"
        >
          <v-layout row wrap>
            <v-flex
              sm12
              md12
              lg12
              xs12
            >
              <span class="label">
                {{ $tc('message.bizgainOnboarding.businessLogo') }}
              </span>
            </v-flex>
            <v-flex
              lg12
              xs12
              sm12
              md12
            >
              <v-layout row wrap>
                <v-flex class="logo-preview" shrink v-if="isLogoPreview">
                  <v-card flat class="selected-logo-preview d-flex elevation-2">
                    <v-layout v-if="businessLogo && !isImageUploading">
                      <v-img
                        contain
                        :src="businessLogo.url"
                        aspect-ratio="1"
                        class="white"
                      />
                    </v-layout>
                    <v-layout
                      v-else-if="isImageUploading"
                      fill-height
                      align-center
                      justify-center
                      ma-0
                    >
                      <v-progress-circular
                        :size="40"
                        :width="3"
                        indeterminate
                        color="primary"
                      />
                    </v-layout>
                    <span
                      v-if="businessLogo && !isImageUploading"
                      class="white--text grey lighten-1 px-1 caption logo-dimension"
                    >
                      {{
                        `${businessLogo.dimension.height} x ${
                          businessLogo.dimension.width
                        }`
                      }}
                    </span>
                  </v-card>
                </v-flex>
                <v-flex xs8 ml-2>
                  <business-images
                    @firstImageProcessed="setIsFirstImageLoaded"
                    @logoSelected="setLogoFromSuggestions"
                    @uploadImage="uploadLogo"
                    @showInvalidBusinessLogoAlert="enableInvalidLogoAlert"
                    @setIsImageUploading="setIsImageUploading"
                    @initialize="setInitializationCompleted"
                    :is-small-suggestion="true"
                    :is-google-map-image-set="false"
                    ref="businessImage"
                  >
                    <template v-slot:open-dialog>
                      <span class="text-decoration-underline primary--text">
                        {{ $t('message.bizgainOnboarding.seeMore') }}
                      </span>
                    </template>
                  </business-images>
                  <span
                    v-if="!(isFirstImageProcessed || isImagesInitialized)"
                  >Loading suggestions...</span>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
          <v-layout v-if="isNotRecommendedDimension">
            <alert
              :icon-color="'primary'"
              :icon-width="`${!$vuetify.breakpoint.xsOnly ? 'xs1' : 'xs2'}`"
              :outline="true"
              :outline-color="'primary'"
              :message="
                $t('message.bizgainOnboarding.businessLogoRecommendation', {
                  height: recommendedLogoDimension.height,
                  width: recommendedLogoDimension.width
                })
              "
              :col-width="'xs12 md8 sm12 lg8'"
            />
          </v-layout>
          <v-layout v-if="isInvalidLogoAlertVisible" class="mt-2">
            <bizgain-message-component
              :type="'error'"
              :message="$t('message.uploadLogoValidationMesssage')"
            />
          </v-layout>
        </v-flex>
        <v-flex xs12 pt-3 class="business-type-services">
          <div class="label">
            {{ $tc('message.bizgainOnboarding.businessType') }}
          </div>
          <business-type
            :is-summary="false"
            :hide-business-type-label="true"
            @save-businessType="updateBusinessType"
          />
        </v-flex>
        <v-flex xs12 pb-1 pt-2>
          <div class="label">
            {{ $tc('message.bizgainOnboarding.businessLocation') }}
          </div>
          <business-location
            @get-location="updateLocationLoadingStatus"
            @updated-location="updateNewLocation"
            :existing-business-location="false"
          />
          <div id="g-map-container" />
        </v-flex>
      </v-flex>
    </template>
    <template v-slot:btn-content>
      <v-flex xs12>
        <v-layout :class="{ 'column reverse': $vuetify.breakpoint.xsOnly }">
          <v-flex
            :class="{
              'xs2 nxt-btn-margin mt-0': !$vuetify.breakpoint.xsOnly,
              xs12: $vuetify.breakpoint.xsOnly,
              'pl-2': $vuetify.breakpoint.smOnly
            }"
          >
            <v-btn
              depressed
              class="mx-0 next-btn-color primary-btn-xs"
              :disabled="
                !postalCode ||
                  !city ||
                  isLocationLoading ||
                  errors.items.length > 0 ||
                  !businessType
              "
              :loading="isLocationDetailsUpdate"
              @click="checkBusinessLocationRegion"
            >
              {{ $t('message.bizgainOnboarding.next') }}
            </v-btn>
          </v-flex>
        </v-layout>
      </v-flex>
    </template>
  </bizgain-component-layout>
</template>
<script>
import {
  VFlex,
  VBtn,
  VTextField,
  VLayout,
  VCard,
  VImg,
  VProgressCircular
} from 'vuetify/lib';
import BizgainComponentLayout from 'Components/BizgainComponentLayout/BizgainComponentLayout.vue';
import BizgainMessageComponent from 'Components/BizgainMessageComponent/BizgainMessageComponent.vue';
import { mapActions, mapGetters } from 'vuex';
import clearErrorByField from 'Mixins/clearErrorByField';
import Alert from 'Components/Alert/Alert';
import { ApiService } from 'Features/bizgainOnboarding/services/apiService';
import {
  bizgainOnboardingRoutes,
  locationType,
  recommendedLogoDimension,
  launchState
} from 'Features/bizgainOnboarding/constants';
const { getGeoLocation, uploadImage } = new ApiService();
import {
  createImageFormDataRequest,
  getDimensionOfImage
} from 'Features/bizgainOnboarding/helpers/commonHelper';
import { imageTypes } from 'Features/bizgainOnboarding/constants';
import AppConfig from 'Constants/AppConfig';
import BusinessImages from 'Features/bizgainOnboarding/components/BusinessImages';
import { Chat } from 'Mixins/chat';
import { Validator } from 'vee-validate';
import onboardingRedirect  from 'Mixins/onboardingRedirect';
import CurrencyPreferenceDialog from '../../../components/CurrencyPreferenceDialog';
import BusinessLocation from '../../../../../components/BusinessLocation/BusinessLocation.vue';
import BusinessType from 'Components/BusinessType/BusinessType.vue';
import { GoogleMapHelper } from 'Features/bizgainOnboarding/helpers/googleMapHelper';

export default {
  mixins: [clearErrorByField, Chat, onboardingRedirect],
  components: {
    VFlex,
    VBtn,
    VTextField,
    VLayout,
    VCard,
    VImg,
    BizgainComponentLayout,
    VProgressCircular,
    Alert,
    BusinessImages,
    BizgainMessageComponent,
    CurrencyPreferenceDialog,
    BusinessLocation,
    BusinessType
  },
  data() {
    return {
      name: null,
      newBusinessDetails: null,
      oldPostalCode: null,
      postalCode: null,
      areaName: null,
      city: null,
      addressLine: null,
      state: null,
      googleMapHelper: null,
      showMap: false,
      country: null,
      longitude: null,
      latitude: null,
      bizgainOnboardingRoutes,
      isLocationDetailsUpdate: false,
      currencyId: null,
      newCurrencySymbol: null,
      dxLocationId: null,
      appConfig: AppConfig,
      recommendedLogoDimension,
      imageTypes,
      countryCode: null,
      dialog: false,
      isInvalidLogoAlertVisible: false,
      isImageUploading: false,
      isCurrencyPreferenceDialog: false,
      isFirstImageProcessed: false,
      isImagesInitialized: false,
      isLocationLoading: false,
      businessType: null
    };
  },
  computed: {
    ...mapGetters([
      'isCustomUploadedBusinessLogo',
      'businessName',
      'businessLocation',
      'businessDetails',
      'isError',
      'currencySymbol',
      'businessLogo',
      'currentBusinessLogo',
      'isRegionChangePromptTriggered',
      'region',
      'validRegions',
      'regionConfig'
    ]),
    isNotRecommendedDimension() {
      return (
        !this.isImageUploading &&
        this.businessLogo &&
        (this.businessLogo.dimension.height <
          this.recommendedLogoDimension.height ||
          this.businessLogo.dimension.width <
            this.recommendedLogoDimension.width)
      );
    },
    isLogoPreview() {
      if (!this.businessLogo && !this.isImageUploading) {
        return false;
      } else {
        return true;
      }
    },
    isLogoChanged() {
      if (this.currentBusinessLogo !== null && this.businessLogo !== null) {
        if (
          this.currentBusinessLogo.url === this.businessLogo.url &&
          this.currentBusinessLogo.sessionId === this.businessDetails.sessionId
        ) {
          return false;
        } else {
          return true;
        }
      } else {
        return true;
      }
    }
  },
  created() {
    if (this.businessName) {
      this.name = this.businessName;
    }
    if (this.businessLocation) {
      this.addressLine = this.businessLocation.street;
      this.postalCode = this.businessLocation.zip;
      this.areaName = this.businessLocation.areaName;
      this.city = this.businessLocation.city;
      this.state = this.businessLocation.state;
      this.oldPostalCode = this.postalCode;
      this.country = this.businessLocation.country;
      this.longitude = this.businessLocation.longitude;
      this.latitude = this.businessLocation.latitude;
      this.currencyId = this.businessLocation.currencyId;
      this.dxLocationId = this.businessLocation.dxLocationId;
    }
    if (this.currencySymbol) {
      this.newCurrencySymbol = this.currencySymbol;
    }

    const dict = {
      custom: {
        businessName: {
          max: this.$t(
            'message.bizgainOnboarding.businessNameValidationMessage'
          )
        }
      }
    };

    Validator.localize('en', dict);
    Validator.extend('emojiValidation', {
      getMessage: () =>
        this.$t('message.bizgainOnboarding.specialCharacterValidation'),
      validate: (value) => {
        return this.validateBusinessName(value);
      }
    });

    this.setCurrentRoute(bizgainOnboardingRoutes.BusinessLocationDetails.name);
    this.googleMapHelper = new GoogleMapHelper();
  },
  methods: {
    ...mapActions([
      'setCurrentRoute',
      'patchBusinessDetails',
      'updateMessage',
      'clearMessages',
      'setBusinessLogo',
      'setCurrentBusinessLogo',
      'setIsCustomUploadedBusinessLogo',
      'setIsBusinessLogoModalEnabled',
      'setRegionChangePromptTriggered',
      'setCurrentSection'
    ]),
    enableInvalidLogoAlert(value) {
      this.isInvalidLogoAlertVisible = value;
    },
    async uploadLogo(file) {
      if (file !== undefined) {
        this.isImageUploading = true;
        this.setBusinessLogo(null);
        let logoFormData = createImageFormDataRequest(
          this.businessDetails.sessionId,
          0,
          file,
          null,
          this.imageTypes.logo,
          file.name
        );
        try {
          this.clearMessages();
          let response = await uploadImage(logoFormData);
          this.setBusinessLogo(response.data.originLogoDetails);
          if (this.businessDetails) {
            this.newBusinessDetails = JSON.parse(
              JSON.stringify(this.businessDetails)
            );
          }
          var dimension =
            response.data.originLogoDetails.dimension.width +
            'x' +
            response.data.originLogoDetails.dimension.height;
          var uploadImageData = {
            imageUrl: response.data.originLogoDetails.url,
            dimensions: dimension,
            type: imageTypes.logo
          };
          this.newBusinessDetails.uploadedImages.push(uploadImageData);
          let payload = {
            sessionId: this.businessDetails.sessionId,
            oldBusinessDetails: this.businessDetails,
            newBusinessDetails: this.newBusinessDetails
          };
          await this.patchBusinessDetails(payload);
          this.$refs.businessImage.addCustomUploadedImage(uploadImageData);
          this.setIsCustomUploadedBusinessLogo(true);
        } catch {
          this.updateMessage({
            type: 'error',
            message: this.$t(
              'message.bizgainOnboarding.failedToUpdateBusinessLogo'
            )
          });
        } finally {
          this.isImageUploading = false;
        }
      }
    },
    async setLogoFromSuggestions(image) {
      try {
        this.isImageUploading = true;
        if (!image.dimension) {
          let imgDimension = await getDimensionOfImage(image.url);
          image.dimension = {
            height: imgDimension.naturalHeight,
            width: imgDimension.naturalWidth
          };
        }
        this.setBusinessLogo({ url: image.url, dimension: image.dimension });
        this.setIsCustomUploadedBusinessLogo(false);
      } finally {
        this.isImageUploading = false;
      }
    },
    setIsFirstImageLoaded() {
      this.isFirstImageProcessed = true;
    },
    setInitializationCompleted() {
      this.isImagesInitialized = true;
    },
    setIsImageUploading(val) {
      this.isImageUploading = val;
    },
    async checkBusinessLocationRegion() {
      if (this.country) {
        let selectedCountry = this.validRegions.find(
          (item) => item.country === this.country
        );
        if (
          selectedCountry &&
          selectedCountry.region != this.region &&
          !this.isRegionChangePromptTriggered
        ) {
          this.isCurrencyPreferenceDialog = true;
          this.setRegionChangePromptTriggered(true);
        } else {
          await this.updateBusinessLocation();
        }
      } else {
        await this.updateBusinessLocation();
      }
    },
    async handleRegionSelected() {
      await this.updateBusinessLocation();
    },
    async updateBusinessLocation() {
      // business category here
      let isValid = await this.$validator.validate();
      if (isValid) {
        try {
          this.isLocationDetailsUpdate = true;

          let logoFormData = new FormData();
          logoFormData.append('SessionId', this.businessDetails.sessionId);
          logoFormData.append('TemplateId', 0);
          logoFormData.append('ImageType', this.imageTypes.logo);
          if (!this.businessLogo) {
            logoFormData.append('ImageName', 'PlaceHolderBusinessLogo');
          } else {
            logoFormData.append('ImageUrl', this.businessLogo.url);
          }
          logoFormData.append('IsAsync', true);
          uploadImage(logoFormData);

          this.setCurrentBusinessLogo({
            url: this.businessLogo ? this.businessLogo.url : null,
            sessionId: this.businessDetails.sessionId
          });
          if (this.businessDetails) {
            this.newBusinessDetails = JSON.parse(
              JSON.stringify(this.businessDetails)
            );
            this.newBusinessDetails.location = {
              city: this.city,
              zip: this.postalCode
            };
            if (!this.businessLogo) {
              this.newBusinessDetails.isBusinessLogoAvailable = false;
            } else {
              this.newBusinessDetails.isBusinessLogoAvailable = true;
            }
            this.setIsBusinessLogoModalEnabled(false);
            if (this.currencyId && this.newCurrencySymbol) {
              this.newBusinessDetails.location.currencyId = this.currencyId;
              this.newBusinessDetails.currencySymbol = this.newCurrencySymbol;
            } else {
              let request = {
                address: this.city,
                componentRestrictions: {
                  postalCode: this.postalCode
                }
              };
              let geoCodeData = await this.googleMapHelper.getGeoCodeData(
                request
              );
              if (geoCodeData && geoCodeData.countryCode) {
                let locationData = await getGeoLocation(
                  null,
                  geoCodeData.countryCode,
                  locationType.country
                );
                if (
                  locationData &&
                  locationData.currencyId &&
                  locationData.currencySymbol
                ) {
                  this.newBusinessDetails.location.currencyId =
                    locationData.currencyId;
                  this.newBusinessDetails.currencySymbol =
                    locationData.currencySymbol;
                  this.country = locationData.countryName;
                }
                this.countryCode = geoCodeData.countryCode;
              }
            }
            if (this.dxLocationId) {
              this.newBusinessDetails.location.dxLocationId = this.dxLocationId;
            }
            if (this.addressLine) {
              this.newBusinessDetails.location.street = this.addressLine;
            }
            if (this.state) {
              this.newBusinessDetails.location.state = this.state;
            }
            if (this.country) {
              this.newBusinessDetails.location.country = this.country;
              await this.updateUserProperties({ countryName: this.country });
            }
            if (this.longitude) {
              this.newBusinessDetails.location.longitude = this.longitude;
            }
            if (this.latitude) {
              this.newBusinessDetails.location.latitude = this.latitude;
            }
            if (this.areaName) {
              this.newBusinessDetails.location.areaName = this.areaName;
            }
            if (this.countryCode) {
              this.newBusinessDetails.location.countryCode = this.countryCode;
            }
            if (this.region) {
              this.newBusinessDetails.region = this.region;
            }
            this.newBusinessDetails.name = this.name;

            if (this.newBusinessDetails.category){
              this.newBusinessDetails.category.displayName = this.businessType.value.displayName;
            }
            else{
              this.newBusinessDetails.category = { displayName: this.businessType.value.displayName };
            }
            await this.updateUserProperties({ firstName: `${this.newBusinessDetails && this.newBusinessDetails.name}` });
            await this.redirect({ isAppFlow: true }, {}, false, launchState.BUSINESSTYPESERVICES, this.newBusinessDetails);
            if(this.isError){
              this.isLocationDetailsUpdate = false;
            }
          }
        } catch {
          this.isLocationDetailsUpdate = false;
        }
      }
    },
    discardChanges() {
      this.name = null;
      this.location = null;
      this.addressLine = null;
      this.postalCode = null;
      this.areaName = null;
      this.city = null;
      this.state = null;
      this.postalCode = null;
    },
    validateBusinessName(value) {
      // this is regex for emoji detection. https://stackoverflow.com/questions/10992921/how-to-remove-emoji-code-using-javascript
      if (
        !value.match(
          /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g
        )
      ) {
        return true;
      }
      return false;
    },
    updateNewLocation(updatedLocation) {
      this.addressLine = updatedLocation.addressLine;
      this.areaName = updatedLocation.areaName;
      this.city = updatedLocation.city;
      this.country = updatedLocation.country;
      this.countryCode = updatedLocation.countryCode;
      this.currencyId = updatedLocation.currencyId;
      this.dxLocationId = updatedLocation.dxLocationId;
      this.latitude = updatedLocation.latitude;
      this.longitude = updatedLocation.longitude;
      this.newCurrencySymbol = updatedLocation.newCurrencySymbol;
      this.oldPostalCode = updatedLocation.oldPostalCode;
      this.postalCode = updatedLocation.postalCode;
      this.state = updatedLocation.state;
    },
    updateLocationLoadingStatus(isLocationLoading) {
      this.isLocationLoading = isLocationLoading;
    },
    updateBusinessType(value){
      this.businessType = value;
    }
  }
};
</script>
<style lang="scss" scoped>
.logo-preview {
  padding: 2px;
  position: relative;
}
.logo-dimension {
  position: absolute;
  bottom: 0px;
  right: 0px;
}
.remove-default-font-case {
  text-transform: none;
}
.text-decoration-underline {
  text-decoration: underline;
}
::v-deep.business-location-details {
  .business-location-card {
    .v-textarea {
      .v-input__control {
        .v-input__slot {
          margin: 0;
          .v-text-field__slot {
            textarea {
              line-height: 1.38;
              letter-spacing: 0.16px;
              padding: 0;
            }
          }
          &::before {
            border-style: none;
          }
          &::after {
            border-style: none;
          }
        }
        .v-text-field__details {
          display: none;
        }
      }
    }
  }
  .text-position {
    position: absolute;
  }

  .selected-logo-preview {
    height: 110px;
    width: 110px;
  }

  @media (max-width: 420px) {
    .selected-logo-preview {
      height: 92px;
      width: 92px;
    }
  }
}
</style>
